import { inject } from 'tsyringe'
import { RawLocation } from 'vue-router'
import { VueRouter } from '~/utils/nuxt3-migration'
import { clientOnly } from '~/decorators'
import { containerScoped } from '~/decorators/dependency-container'
import { AvailableLocale, I18nLocale } from '~/models/shared/types'
import DealerSiteService from '~/services/dealers/site/DealerSiteService'
import LocaleService from '~/services/LocaleService'
import CookiesService from '~/services/CookiesService'
import { StoreWithRootState } from '~/store/types'
import { I18N_NS } from '~/store/modules/shared/i18n/state'
import LoggerService from '~/services/LoggerService'
import { carPublicLocales } from '~/constants/i18n'
import {
  storeToken,
  vueRouterToken
} from '~/constants/dependency-injection/tokens'

@containerScoped()
export default class LanguageSwitcher {
  constructor(
    @inject(CookiesService) private cookies: CookiesService,
    @inject(vueRouterToken) private router: VueRouter,
    @inject(LoggerService) private logger: LoggerService,
    @inject(storeToken) private store: StoreWithRootState,
    @inject(LocaleService)
    private localeService: LocaleService,
    @inject(DealerSiteService) private dealerSiteService: DealerSiteService
  ) {}

  @clientOnly
  async handleLanguageChange(
    e: Event,
    aloc: AvailableLocale,
    ignoreActiveLocale: boolean = false
  ) {
    e.preventDefault()
    if (
      this.store.getters[`${I18N_NS}/localeIsActive`](aloc.locale) &&
      !ignoreActiveLocale
    ) {
      return
    }
    try {
      await this.switchTo(aloc)
    } catch (error) {
      this.logger.captureError(error)
    }
  }

  @clientOnly
  switchTo(locale: AvailableLocale) {
    if (this.dealerSiteService.routeIsOfDealerSite()) {
      this.cookies.set('lang', locale.locale)
      window.location.href = window.location.href.toString()
      return
    }

    window.location.href = locale.link
  }

  getCurrentUrlWithLocale({
    locale,
    customRoute = ''
  }: {
    locale: I18nLocale
    customRoute?: any
  }): string {
    const route = customRoute || this.router.currentRoute

    return this.router.resolve({
      ...route,
      query: { ...route.query, lang: locale }
    } as RawLocation).href
  }

  getAvailableLocales(customRoute = null): AvailableLocale[] {
    return carPublicLocales.map(locale => {
      return {
        locale,
        title: this.localeService.getLocaleLabel(locale),
        icon: this.localeService.getLocaleIcon(locale),
        link: this.getCurrentUrlWithLocale({
          locale,
          customRoute
        })
      }
    })
  }
}
